import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@sunstone/ui';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Link } from 'react-router';
import { routes } from '@/lib/routes';

interface DeprecateReleaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: string;
  releaseId: string;
  blockingWorkspaceCount: number;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

/**
 * Confirms deprecating a release. blockingWorkspaceCount comes from a live
 * useCheckReleaseDeprecateBlockers query the parent polls while this dialog
 * is open (tied to `!!deprecateTarget`), not from the deprecate mutation's
 * response - if the mutation itself is rejected by a last-second race (the
 * blocking dependency appearing after this dialog's last poll but before the
 * confirm click lands), that surfaces as a toast via the caller's onError,
 * not through this dialog.
 */
export function DeprecateReleaseDialog({
  open,
  onOpenChange,
  version,
  releaseId,
  blockingWorkspaceCount,
  onConfirm,
  isSubmitting = false,
}: DeprecateReleaseDialogProps) {
  const isBlocked = blockingWorkspaceCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Deprecate {version}
          </DialogTitle>
          <DialogDescription>
            {isBlocked
              ? `${version} is still running on ${blockingWorkspaceCount} workspace${blockingWorkspaceCount === 1 ? '' : 's'}. It can't be deprecated until they upgrade off this version.`
              : `${version} will be hidden from workspace create/upgrade dropdowns and can't be selected again. This can't be undone.`}
          </DialogDescription>
        </DialogHeader>

        {isBlocked && (
          <Link
            to={routes.workspaces(releaseId)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary underline underline-offset-4"
          >
            See workspaces
          </Link>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {isBlocked ? 'Close' : 'Cancel'}
          </Button>
          {!isBlocked && (
            <Button variant="destructive" onClick={onConfirm} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Deprecate
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
